import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { 
  BehaviorSubject, 
  Observable, 
  Subject, 
  Subscription, 
  interval, 
  of,
  timer,
  defer,
  EMPTY
} from 'rxjs';
import { 
  switchMap, 
  takeUntil, 
  tap, 
  catchError, 
  filter,
  distinctUntilChanged,
  map,
  retry,
  shareReplay,
  delay,
  expand
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface TaskProgress {
  id: string;
  displayTitle: string;
  progress: number;
  status: string;
  rootId: string;
  parentId: string;
}

export interface UploadStatusResponse {
  containerName: string;
  stagingId: string;
  collectionId: string;
  collectionName: string;
  simulationName: string;
  simulationStatus: TaskProgress;
  fileStatuses: TaskProgress[];
  isComplete: boolean;
  lastUpdated: Date;
}

export interface BatchUploadStatusRequest {
  containerNames: string[];
}

export interface BatchUploadStatusResponse {
  statuses: UploadStatusResponse[];
  requestedCount: number;
  foundCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class UploadStatusPollingService implements OnDestroy {

  private readonly POLLING_INTERVAL_MS = 1000; // Poll every 10 seconds
  private readonly FAST_POLLING_INTERVAL_MS = 60000; // Fast poll for critical updates
  private readonly MAX_RETRIES = 3;

  private activePolling: Map<string, Subject<void>> = new Map();
  private pollingSubscriptions: Map<string, Subscription> = new Map();

  // Observable for progress updates (similar to SignalR events)
  private _progressUpdate = new Subject<TaskProgress>();
  public progressUpdate$ = this._progressUpdate.asObservable();

  // Observable for simulation-level updates
  private _simulationUpdate = new Subject<UploadStatusResponse>();
  public simulationUpdate$ = this._simulationUpdate.asObservable();

  // Observable for completion events
  private _uploadComplete = new Subject<UploadStatusResponse>();
  public uploadComplete$ = this._uploadComplete.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Start polling for a specific upload container
   * Automatically stops when upload is complete
   * Uses expand operator to ensure next poll only happens after previous response
   */
  startPolling(containerName: string, fastPolling: boolean = false): Observable<UploadStatusResponse> {
    // Stop any existing polling for this container
    this.stopPolling(containerName);

    const stopSignal = new Subject<void>();
    this.activePolling.set(containerName, stopSignal);

    const pollingInterval = fastPolling ? this.FAST_POLLING_INTERVAL_MS : this.POLLING_INTERVAL_MS;

    // Use defer to create a new observable for each poll cycle
    const polling$ = defer(() => this.fetchUploadStatus(containerName)).pipe(
      expand(response => {
        // If response is null or complete, stop polling
        if (!response || response.isComplete) {
          return EMPTY;
        }
        // Wait for the polling interval, then fetch next status
        return timer(pollingInterval).pipe(
          switchMap(() => this.fetchUploadStatus(containerName))
        );
      }),
      takeUntil(stopSignal),
      filter((response): response is UploadStatusResponse => response !== null),
      distinctUntilChanged((prev, curr) => 
        JSON.stringify(prev) === JSON.stringify(curr)
      ),
      tap(response => this.processResponse(response, containerName)),
      shareReplay(1)
    );

    const subscription = polling$.subscribe();
    this.pollingSubscriptions.set(containerName, subscription);

    return polling$;
  }

  /**
   * Start polling for multiple containers at once
   * Uses sequential polling to avoid overlapping requests
   */
  startBatchPolling(containerNames: string[]): Observable<BatchUploadStatusResponse> {
    const stopSignal = new Subject<void>();
    const batchKey = containerNames.join(',');
    this.activePolling.set(batchKey, stopSignal);

    // Use defer and expand for sequential batch polling
    return defer(() => this.fetchBatchUploadStatus(containerNames)).pipe(
      expand(response => {
        // Check if any upload is incomplete
        const hasIncomplete = response?.statuses?.some(s => !s.isComplete) ?? false;
        if (!hasIncomplete) {
          return EMPTY;
        }
        // Wait for polling interval, then fetch next batch
        return timer(this.POLLING_INTERVAL_MS).pipe(
          switchMap(() => this.fetchBatchUploadStatus(containerNames))
        );
      }),
      takeUntil(stopSignal),
      filter((response): response is BatchUploadStatusResponse => response !== null),
      tap(response => {
        response.statuses.forEach(status => {
          this.processResponse(status, status.containerName);
        });
      })
    );
  }

  /**
   * Get current status once (no polling)
   */
  getUploadStatus(containerName: string): Observable<UploadStatusResponse | null> {
    return this.fetchUploadStatus(containerName);
  }

  /**
   * Check if upload is complete (quick check)
   */
  isUploadComplete(containerName: string): Observable<boolean> {
    const url = `${environment.apiEndpoint}/uploadstatus/${containerName}/complete`;
    return this.http.get<{ isComplete: boolean }>(url).pipe(
      map(response => response.isComplete),
      catchError(() => of(false))
    );
  }

  /**
   * Stop polling for a specific container
   */
  stopPolling(containerName: string): void {
    const stopSignal = this.activePolling.get(containerName);
    if (stopSignal) {
      stopSignal.next();
      stopSignal.complete();
      this.activePolling.delete(containerName);
    }

    const subscription = this.pollingSubscriptions.get(containerName);
    if (subscription) {
      subscription.unsubscribe();
      this.pollingSubscriptions.delete(containerName);
    }
  }

  /**
   * Stop all active polling
   */
  stopAllPolling(): void {
    this.activePolling.forEach((subject, key) => {
      subject.next();
      subject.complete();
    });
    this.activePolling.clear();

    this.pollingSubscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
    this.pollingSubscriptions.clear();
  }

  /**
   * Check if polling is active for a container
   */
  isPolling(containerName: string): boolean {
    return this.activePolling.has(containerName);
  }

  /**
   * Get list of actively polling containers
   */
  getActivePollingContainers(): string[] {
    return Array.from(this.activePolling.keys());
  }

  ngOnDestroy(): void {
    this.stopAllPolling();
    this._progressUpdate.complete();
    this._simulationUpdate.complete();
    this._uploadComplete.complete();
  }

  // Private methods

  private fetchUploadStatus(containerName: string): Observable<UploadStatusResponse | null> {
    const url = `${environment.apiEndpoint}/uploadstatus/${containerName}`;
    return this.http.get<UploadStatusResponse>(url).pipe(
      tap(t => {
        console.log('returning uploadstatus', t);
      }),
      retry(this.MAX_RETRIES),
      catchError(error => {
        console.error('Error fetching upload status:', error);
        return of(null);
      })
    );
  }

  private fetchBatchUploadStatus(containerNames: string[]): Observable<BatchUploadStatusResponse | null> {
    const url = `${environment.apiEndpoint}/uploadstatus/batch`;
    const request: BatchUploadStatusRequest = { containerNames };

    return this.http.post<BatchUploadStatusResponse>(url, request).pipe(
      retry(this.MAX_RETRIES),
      catchError(error => {
        console.error('Error fetching batch upload status:', error);
        return of(null);
      })
    );
  }

  private processResponse(response: UploadStatusResponse, containerName: string): void {
    // Emit simulation-level update
    this._simulationUpdate.next(response);

    // Emit simulation progress
    this._progressUpdate.next(response.simulationStatus);

    // Emit file progress updates
    response.fileStatuses.forEach(file => {
      this._progressUpdate.next(file);
    });

    // Check for completion and emit event
    if (response.isComplete) {
      this._uploadComplete.next(response);
      // Auto-stop polling when complete
      this.stopPolling(containerName);
    }
  }
}